import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

const SettingsContext = createContext(null);
const DRAFT_KEY = "hp_draft_settings";
const DRAFT_TS_KEY = "hp_draft_settings_ts";

const FALLBACK = {
    brand_name: "Hahnemann",
    brand_subtitle: "Pharmacy & Lab",
    brand_subtitle_full: "Pharmacy & Laboratory",
    logo_url: "",
    site_tagline: "Gentle. Holistic. Hahnemann.",
    contact_email: "help@hplmedicines.com",
    contact_phone: "+91 00000 00000",
    hero_eyebrow: "Est. tradition · 1796",
    hero_title_line1: "The apothecary,",
    hero_title_line2: "re-imagined",
    hero_title_line3: "for the curious patient.",
    hero_subtitle: "Classical homeopathic medicines, handcrafted in Hahnemann's tradition. Talk to our AI Doctor, browse curated remedies in 30ml–450ml bottles, or book a verified homeopath—online or near you.",
    hero_image_url: "",
    hero_image_caption_label: "Sizes",
    hero_image_caption_value: "30ml — 450ml",
    hero_image_caption_note: "Choose any potency, any size, on every product.",
    ai_promo_image_url: "",
    ai_promo_eyebrow: "Dr. Hahnemann AI",
    ai_promo_title: "Symptoms in. Remedies out.",
    ai_promo_subtitle: "Trained on classical materia medica, our AI assistant interviews you about your symptoms and suggests up to three matching remedies—or refers you to a verified doctor if it's serious.",
    show_internet_doctors: true,
    consultation_fee_online: 500,
    consultation_fee_offline: 700,
    doctor_revenue_share: 0.7,
};

const isDraftUrl = (search) => new URLSearchParams(search || "").get("draft") === "1";

const readDraftRaw = () => {
    try {
        return localStorage.getItem(DRAFT_KEY) || null;
    } catch { return null; }
};

const parseDraft = (raw) => {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
};

export function SettingsProvider({ children }) {
    const location = useLocation();
    // Reactive: if URL no longer has ?draft=1, drop out of draft mode immediately.
    const draftMode = isDraftUrl(location.search);

    const [serverSettings, setServerSettings] = useState(FALLBACK);
    const [draftRaw, setDraftRaw] = useState(() => (isDraftUrl(typeof window !== "undefined" ? window.location.search : "") ? readDraftRaw() : null));
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/settings/public");
            setServerSettings({ ...FALLBACK, ...data });
        } catch {
            setServerSettings(FALLBACK);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Only run the localStorage watcher while in draft mode. Compare raw strings to avoid
    // re-rendering the entire app every 800ms when nothing has actually changed.
    useEffect(() => {
        if (!draftMode) {
            // Make sure we drop any stale draft state once the user leaves draft mode.
            setDraftRaw(null);
            return;
        }
        const sync = () => {
            const next = readDraftRaw();
            setDraftRaw((prev) => (prev === next ? prev : next));
        };
        sync();
        const onStorage = (e) => { if (e.key === DRAFT_KEY) sync(); };
        window.addEventListener("storage", onStorage);
        const t = setInterval(sync, 1000);
        return () => { window.removeEventListener("storage", onStorage); clearInterval(t); };
    }, [draftMode]);

    const draftSettings = useMemo(() => parseDraft(draftRaw), [draftRaw]);

    const settings = useMemo(
        () => ((draftMode && draftSettings) ? { ...serverSettings, ...draftSettings } : serverSettings),
        [draftMode, draftSettings, serverSettings],
    );

    const setDraft = (next) => {
        try {
            const json = JSON.stringify(next);
            localStorage.setItem(DRAFT_KEY, json);
            localStorage.setItem(DRAFT_TS_KEY, String(Date.now()));
            // Same-tab writes don't trigger storage events, so update local state too.
            setDraftRaw(json);
        } catch {}
    };
    const clearDraft = () => {
        try { localStorage.removeItem(DRAFT_KEY); localStorage.removeItem(DRAFT_TS_KEY); } catch {}
        setDraftRaw(null);
    };

    const value = useMemo(() => ({ settings, loading, refresh, draftMode, setDraft, clearDraft }),
        [settings, loading, refresh, draftMode]);

    return (
        <SettingsContext.Provider value={value}>
            {draftMode && (
                <div className="bg-amber-500 text-stone-900 text-center py-2 text-xs tracking-widest uppercase font-medium" data-testid="draft-banner">
                    Preview mode · showing unsaved changes · close this tab to exit
                </div>
            )}
            {children}
        </SettingsContext.Provider>
    );
}

export const useSettings = () => useContext(SettingsContext) || { settings: FALLBACK, loading: false, refresh: () => {}, draftMode: false, setDraft: () => {}, clearDraft: () => {} };
